import { waitDialog } from 'dialogs';
import { recordToEdit } from 'edit/recordToEdit';
import type { CreateTarget } from 'actions';
import { getTrigger, type Trigger } from 'api/triggers/endpoints/getTrigger';
import EditTrigger from '../editTrigger';

export type ShowEditTriggerArgs = { readonly mode: 'create'; readonly createIn?: CreateTarget } | { readonly mode: 'edit'; readonly id: number };

/**
 * Open the trigger create / edit dialog and resolve when it closes (so the
 * caller can refresh). Drives the existing EditTrigger externally (`hideTrigger`
 * + `forceShow` + `onRequestClose`).
 *
 * For edit the record is fetched HERE and handed in, rather than the dialog
 * fetching itself through the list slice. Three reasons, in ascending order of
 * how much they cost when ignored:
 *
 * - The by-id read returns the NESTED shape while the list holds FLAT rows, so
 *   the old upsert put two different shapes in one array — and the list table
 *   renders seven columns that exist only on the flat row, which the upsert
 *   blanked for that row until the post-close refresh.
 * - A failed read was silent (`fetchTrigger` rejected with `''` and the slice had
 *   no `rejected` case), leaving the form on a stale row that the next save would
 *   write back. `recordToEdit` gives each outcome its own sentence and opens
 *   nothing.
 * - The form no longer seeds twice — once from whatever flat row the list had,
 *   then again from the record — so a value typed in that window is no longer
 *   overwritten when the read lands.
 *
 * The cost, stated plainly: nothing indicates the fetch is in flight, so there is
 * a gap between the click and the dialog. That is `recordToEdit`'s shape, shared
 * with five other dialogs, and it is what an activity indicator fixes for all of
 * them at once. `EditModel` solves it properly by opening first and rendering a
 * spinner until the entity exists — which is this dialog's eventual migration.
 */
export const showEditTrigger = async (args: ShowEditTriggerArgs): Promise<void> => {
  if (args.mode === 'create') {
    await waitDialog<void>((ok) => <EditTrigger newTrigger hideTrigger forceShow onRequestClose={ok} createIn={args.createIn} />);
    return;
  }
  const trigger: Trigger | undefined = await recordToEdit('Trigger', () => getTrigger(args.id));
  if (trigger === undefined) return;
  await waitDialog<void>((ok) => <EditTrigger trigger={trigger} hideTrigger forceShow onRequestClose={ok} />);
};
